import { addDays, addHours, addMinutes, addMonths, addWeeks, addYears, differenceInDays, differenceInHours, differenceInMonths, differenceInWeeks, differenceInYears } from "date-fns";
import { IDateRange } from "../../../common/models/IDateRange";
import { IKeyValuePair } from "../../../common/models/KeyValuePair";
import { AlarmScheduleUnitEnum, DayOfWeekEnum, IAlarmScheduleModel, IBlackoutSchedule, defaultAlarmSchedule } from "../../../services/alarm-schedule-data-service/Models";
import { IReferenceTimeZoneDTO } from "../../../services/global-data-service/Models";

export enum AlarmScheduleAdminMode {
    Add = 0,
    Edit = 1
}

export enum StepEnum {
    Properties = 1,
    Usage = 2,
    Summary = 3
}

const blackoutHours: string [] = [
    "12:00 AM",
    "01:00 AM",
    "02:00 AM",
    "03:00 AM",
    "04:00 AM",
    "05:00 AM",
    "06:00 AM",
    "07:00 AM",
    "08:00 AM",
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "01:00 PM",
    "02:00 PM",
    "03:00 PM",
    "04:00 PM",
    "05:00 PM",
    "06:00 PM",
    "07:00 PM",
    "08:00 PM",
    "09:00 PM",
    "10:00 PM",
    "11:00 PM",
];

const blackoutDayOptions: IKeyValuePair<string, string>[] = [
    { Key: 'S', Value: 'sunday'},
    { Key: 'M', Value: 'monday'},
    { Key: 'T', Value: 'tuesday'},
    { Key: 'W', Value: 'wednesday'},
    { Key: 'T', Value: 'thursday'},
    { Key: 'F', Value: 'friday'},
    { Key: 'S', Value: 'saturday'},
];


export enum OverrideDurationEnum {
    UseDuration = 0,
    UseEndDate = 1
}

export type IntervalType = {
    value: number;
    unit: AlarmScheduleUnitEnum
}

export type FormBlackoutScheduleType = {

    id: number;

    sunday: boolean;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;

    startHour: string;
    endHour: string;
}

export type AlarmScheduleFormType = {
    dateRange: IDateRange;
    duration: IntervalType;
    repeat: IntervalType;
    timezoneIndex: number;

    overrideDuration: OverrideDurationEnum;

    setBlackoutHours: boolean;
    setBlackoutDays: boolean;

    blackoutSchedules: FormBlackoutScheduleType[];

} & Omit<IAlarmScheduleModel, 'start' | 'end' | 'duration' | 'durationUnit' | 'repeatInterval' | 'repeatIntervalUnit' | 'blackoutSchedules'>;


const defaultAlarmScheduleForm = {...defaultAlarmSchedule,
    overrideDuration: OverrideDurationEnum.UseDuration,
    dateRange: {
        startDate: defaultAlarmSchedule.start,
        endDate: defaultAlarmSchedule.end
    },
    duration: {
        value: defaultAlarmSchedule.duration,
        unit: defaultAlarmSchedule.durationUnit
    },
    repeat: {
        value: defaultAlarmSchedule.repeatInterval,
        unit: defaultAlarmSchedule.repeatIntervalUnit
    },
    timezoneIndex: 20,
    setBlackoutDays: false,
    setBlackoutHours: false,
    blackoutSchedules: [{
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        startHour: blackoutHours[0],
        endHour: blackoutHours[0]
    }]
}


const alarmScheduleUnits: IKeyValuePair<AlarmScheduleUnitEnum, string>[] = [
    {Key: AlarmScheduleUnitEnum.Hour, Value: "Hours"},
    {Key: AlarmScheduleUnitEnum.Day, Value: "Days"},
    {Key: AlarmScheduleUnitEnum.Week, Value: "Weeks"},
    {Key: AlarmScheduleUnitEnum.Month, Value: "Months"},
    {Key: AlarmScheduleUnitEnum.Year, Value: "Years"},
];

const AlarmScheduleEditorUtils = {
    mapToModel: (formData: AlarmScheduleFormType,
            timezones: IReferenceTimeZoneDTO[]): IAlarmScheduleModel => {


        const timezone = timezones.find(o => o.id === formData.timezoneIndex)                ;

        const model: IAlarmScheduleModel = {
            id: formData.id,
            name: formData.name,
            start: formData.dateRange.startDate as Date,
            end: formData.dateRange.endDate as Date,
            duration: formData.duration.value,
            durationUnit: formData.duration.unit,
            repeatInterval: formData.repeat.value,
            repeatIntervalUnit: formData.repeat.unit,
            linkedAlarmSchemes: formData.linkedAlarmSchemes,
            timezone: timezone?.standardName ?? formData.timezone,
            olsonTimezone: timezone?.olsonTimezone ?? formData.olsonTimezone,
            blackoutSchedules: !formData.setBlackoutDays && !formData.setBlackoutHours ?
                        undefined : 
                        formData.blackoutSchedules.map(v => {

                            const daysOfWeek: number[] | undefined = [];

                            if (formData.setBlackoutDays) {

                                if (v.sunday) {
                                    daysOfWeek.push(DayOfWeekEnum.Sunday);
                                }

                                if (v.monday) {
                                    daysOfWeek.push(DayOfWeekEnum.Monday);
                                }
                                
                                if (v.tuesday) {
                                    daysOfWeek.push(DayOfWeekEnum.Tuesday);
                                }

                                if (v.wednesday) {
                                    daysOfWeek.push(DayOfWeekEnum.Wednesday);
                                }

                                if (v.thursday) {
                                    daysOfWeek.push(DayOfWeekEnum.Thursday);
                                }

                                if (v.friday) {
                                    daysOfWeek.push(DayOfWeekEnum.Friday);
                                }

                                if (v.saturday) {
                                    daysOfWeek.push(DayOfWeekEnum.Saturday);
                                }
                            }

                            const item = {
                                daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : undefined,
                                startHour: formData.setBlackoutHours ? v.startHour : undefined,
                                endHour: formData.setBlackoutHours ? v.endHour : undefined,
                                
                            } as IBlackoutSchedule;

                            return item;
                        })
        };

        return model;
    },
    mapToFormValues: (model: IAlarmScheduleModel,
        timezoneID: number,
        timezones: IReferenceTimeZoneDTO[]
    ): AlarmScheduleFormType  => {

        const timezone = timezones.find(o => o.id === timezoneID);

        // currently, requirements only allow for one schedule
        const blackoutSchedules = model.blackoutSchedules?.map((o) => {

            return {
                sunday: o.daysOfWeek?.find(v => v === DayOfWeekEnum.Sunday) !== undefined,
                monday: o.daysOfWeek?.find(v => v === DayOfWeekEnum.Monday) !== undefined,
                tuesday: o.daysOfWeek?.find(v => v === DayOfWeekEnum.Tuesday) !== undefined,
                wednesday: o.daysOfWeek?.find(v => v === DayOfWeekEnum.Wednesday) !== undefined,
                thursday: o.daysOfWeek?.find(v => v === DayOfWeekEnum.Thursday) !== undefined,
                friday: o.daysOfWeek?.find(v => v === DayOfWeekEnum.Friday) !== undefined,
                saturday: o.daysOfWeek?.find(v => v === DayOfWeekEnum.Saturday) !== undefined,
                startHour: o.startHour ?? blackoutHours[0],
                endHour: o.endHour ?? blackoutHours[1]
    
            } as FormBlackoutScheduleType
        }) ?? [];

        const hasBlackoutDays = blackoutSchedules.find(o => o.sunday ||
            o.monday ||
            o.tuesday ||
            o.wednesday ||
            o.thursday ||
            o.friday ||
            o.saturday) !== undefined;

        const hasBlackoutHours = model.blackoutSchedules?.find(o => o.startHour && o.endHour) !== undefined; 

        if (blackoutSchedules.length === 0) {

            blackoutSchedules.push({
                id: 0,
                sunday: false,
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false,
                startHour: blackoutHours[0],
                endHour: blackoutHours[1]
            });
        }

        const form = {
            id: model.id,
            name: model.name,
            linkedAlarmSchemes: model.linkedAlarmSchemes,
            timezone: model.timezone,
            olsonTimezone: model.olsonTimezone,
            overrideDuration: OverrideDurationEnum.UseDuration,
            dateRange: {
                startDate: new Date(model.start),
                endDate: new Date(model.end)
            },
            duration: {
                value: model.duration,
                unit: model.durationUnit
            },
            repeat: {
                value: model.repeatInterval,
                unit: model.repeatIntervalUnit
            },
            timezoneIndex: timezoneID,
            setBlackoutDays: hasBlackoutDays,
            setBlackoutHours: hasBlackoutHours,
            blackoutSchedules: blackoutSchedules
        } as AlarmScheduleFormType;

        if (model.id < 0) {
            form.timezone = timezone?.standardName ?? '';
            form.olsonTimezone = timezone?.olsonTimezone ?? '';
        }

        return form;
    },
    getRepeatDays: (repeat: IntervalType): number => {

        const startDate = new Date();

        switch (repeat.unit) {
            case AlarmScheduleUnitEnum.Hour:
                return differenceInDays(addHours(startDate, repeat.value), startDate);

            case AlarmScheduleUnitEnum.Day:
                return differenceInDays(addDays(startDate, repeat.value), startDate);                
    
            case AlarmScheduleUnitEnum.Week:
                return differenceInDays(addWeeks(startDate, repeat.value), startDate);                

            case AlarmScheduleUnitEnum.Month:
                return differenceInDays(addMonths(startDate, repeat.value), startDate);                

            case AlarmScheduleUnitEnum.Year:
                return differenceInDays(addYears(startDate, repeat.value), startDate);                                

            default: 
                return 0;
        }
    },
    getDayOfWeekLabel: (dayOfWeek:  DayOfWeekEnum): string => {
        switch (dayOfWeek) {
            case DayOfWeekEnum.Sunday:
                return "S";

            case DayOfWeekEnum.Monday:
                return "M";

            case DayOfWeekEnum.Tuesday:
                return "T";

            case DayOfWeekEnum.Wednesday:
                return "W";

            case DayOfWeekEnum.Thursday:
                return "T";

            case DayOfWeekEnum.Friday:
                return "F";

            case DayOfWeekEnum.Saturday:
                return "S";

            default: 
                return "";
                                                                                    
        }
    },

    calculateDuration: (startDate: Date, endDate: Date): IntervalType | undefined => {


        // we have to adjust the end date, by one minute for the various diffenceX functions
        // to account for the end date corectly
        const adjustedEndDate = addMinutes(endDate, 1);

        let unit = AlarmScheduleUnitEnum.Year;
        let value = differenceInYears(adjustedEndDate, startDate);

        if (!value) {
            unit = AlarmScheduleUnitEnum.Month;
            value = differenceInMonths(adjustedEndDate, startDate);
        }

        if (!value) {
            unit = AlarmScheduleUnitEnum.Week;
            value = differenceInWeeks(adjustedEndDate, startDate);
        }

        if (!value) {
            unit = AlarmScheduleUnitEnum.Day;
            value = differenceInDays(adjustedEndDate, startDate);
        }

        if (!value) {
            unit = AlarmScheduleUnitEnum.Hour;
            value = differenceInHours(adjustedEndDate, startDate);
        }

        if (!value) {
            // cannot determine the duration, must be less than 1 hour
            return undefined;
        }

        return {
            value: value,
            unit: unit
        }
    },
    calculateEndDate: (startDate: Date, duration: IntervalType): Date => {

        switch (duration.unit) {
            case AlarmScheduleUnitEnum.Hour:
                return addHours(startDate, duration.value);

            case AlarmScheduleUnitEnum.Day:
                return addDays(startDate, duration.value);
    
            case AlarmScheduleUnitEnum.Week:
                return addWeeks(startDate, duration.value);

            case AlarmScheduleUnitEnum.Month:
                return addMonths(startDate, duration.value);

            case AlarmScheduleUnitEnum.Year:
                return addYears(startDate, duration.value);

            default: 
                return startDate;
        }
   }
}

export default AlarmScheduleEditorUtils;

export {alarmScheduleUnits, blackoutHours, defaultAlarmScheduleForm, blackoutDayOptions}